import { useEffect, useMemo, useState } from "react";

const EMPTY_ITEMS = [];

/**
 * Client-side pagination for in-memory lists.
 * @param {Array} items
 * @param {number} pageSize
 * @returns {{ page, setPage, pageCount, paginatedItems, totalItems }}
 */
export default function usePagination(items, pageSize = 25) {
    const [page, setPage] = useState(1);
    const safeItems = Array.isArray(items) ? items : EMPTY_ITEMS;
    const totalItems = safeItems.length;
    const pageCount = Math.max(1, Math.ceil(totalItems / pageSize) || 1);

    useEffect(() => {
        setPage(1);
    }, [safeItems]);

    useEffect(() => {
        if (page > pageCount) {
            setPage(pageCount);
        }
    }, [page, pageCount]);

    const paginatedItems = useMemo(() => {
        const start = (page - 1) * pageSize;
        return safeItems.slice(start, start + pageSize);
    }, [safeItems, page, pageSize]);

    return { page, setPage, pageCount, paginatedItems, totalItems };
}

/** MUI Pagination styling aligned with the admin dark + gold theme. */
export const paginationSx = {
    py: 2,
    display: "flex",
    justifyContent: "center",
    bgcolor: "#0a0c0f",
    borderTop: "1px solid rgba(255,255,255,0.08)",
    "& .MuiPaginationItem-root": {
        color: "rgba(240, 237, 232, 0.85)",
    },
    "& .MuiPaginationItem-root:hover": {
        bgcolor: "rgba(232, 201, 126, 0.12)",
    },
    "& .MuiPaginationItem-root.Mui-selected": {
        bgcolor: "#e8c97e",
        color: "#0a0c0f",
        fontWeight: 700,
        "&:hover": {
            bgcolor: "#d4b56a",
        },
    },
    "& .MuiPaginationItem-ellipsis": {
        color: "rgba(240, 237, 232, 0.5)",
    },
};

export function paginationRangeLabel(page, pageSize, totalItems, noun = "items") {
    if (!totalItems) {
        return `Showing 0 of 0 ${noun}`;
    }
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, totalItems);
    return `Showing ${start}–${end} of ${totalItems} ${noun}`;
}
